import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CircleSlash,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  MapPinOff,
  PackageCheck,
  Route as RouteIcon,
  Timer,
  Warehouse,
  X,
} from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../../../components/ui/Badge";
import { Table, TBody, TD, TH, THead, TR } from "../../../components/ui/Table";
import { EmptyState } from "../../../components/ui/EmptyState";
import { cn } from "../../../utils/cn";
import { useTheme } from "../../../hooks/useTheme";
import { formatNumberCL } from "../../../utils/formatters";
import { DayBar } from "./DayBar";
import { usePdfViewer } from "./usePdfViewer";
import { hhmm, horaDe, iniciales, primerPickup } from "./recoleccionesShared";

// ─────────────────────────── helpers de datos ───────────────────────────

// Suma numérica tolerante: los campos por parada llegan como string/""/null.
function sumNum(items, key) {
  let out = 0;
  for (const it of items) {
    const v = it[key];
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (!Number.isNaN(n)) out += n;
  }
  return out;
}

/**
 * Cortes de la banda de KPIs.
 *
 * `pendientes` excluye explícitamente las anuladas: el repartidor ya resolvió esa parada
 * —llegó y el comercio no tenía pedidos— así que contarla como pendiente le muestra al
 * operador trabajo que no existe. Antes era `estado !== "ejecutada"`, que las barría a
 * todas dentro.
 */
const CORTE_PRED = {
  realizadas: (p) => p.estado === "ejecutada",
  pendientes: (p) => p.estado !== "ejecutada" && p.estado !== "anulada",
  anuladas: (p) => p.estado === "anulada",
  sin_asignar: (p) => p.sinChofer,
};

const CORTE_LABEL = {
  realizadas: "Realizadas",
  pendientes: "Pendientes",
  anuladas: "Anuladas",
  sin_asignar: "Sin asignar",
};

const CORTE_TITULO = {
  realizadas: "Retiros realizados del día",
  pendientes: "Paradas aún sin retiro registrado",
  anuladas: "Paradas anuladas: el comercio no tenía pedidos",
  sin_asignar: "Paradas de rutas sin chofer asignado",
};

const CORTE_TONE = {
  realizadas: "success",
  pendientes: "neutral",
  anuladas: "muted",
  sin_asignar: "error",
};

// Tonos de las tarjetas KPI (mismo vocabulario visual que el mockup mu-portal).
const TONO = {
  brand: {
    inactivo: "border-border bg-bg hover:border-brand-300",
    activo: "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20",
    icono: "text-brand-600 bg-brand-50",
    barra: "bg-brand-500",
  },
  warning: {
    inactivo: "border-border bg-bg hover:border-status-warning/40",
    activo: "border-status-warning bg-status-warning-soft ring-2 ring-status-warning/20",
    icono: "text-status-warning bg-status-warning-soft",
    barra: "bg-status-warning",
  },
  error: {
    inactivo: "border-border bg-bg hover:border-status-error/40",
    activo: "border-status-error bg-status-error-soft ring-2 ring-status-error/20",
    icono: "text-status-error bg-status-error-soft",
    barra: "bg-status-error",
  },
  success: {
    inactivo: "border-border bg-bg hover:border-status-success/40",
    activo: "border-status-success bg-status-success-soft ring-2 ring-status-success/20",
    icono: "text-status-success bg-status-success-soft",
    barra: "bg-status-success",
  },
  info: {
    inactivo: "border-border bg-bg hover:border-status-info/40",
    activo: "border-status-info bg-status-info-soft ring-2 ring-status-info/20",
    icono: "text-status-info bg-status-info-soft",
    barra: "bg-status-info",
  },
  neutral: {
    inactivo: "border-border bg-bg hover:border-fg-subtle",
    activo: "border-fg-muted bg-bg-muted ring-2 ring-fg-muted/15",
    icono: "text-fg-muted bg-bg-muted",
    barra: "bg-fg-muted",
  },
};

const RUTA_ESTADO_LABEL = {
  sin_iniciar: "Sin iniciar",
  en_curso: "En curso",
  completada: "Completada",
};
const RUTA_ESTADO_TONE = {
  sin_iniciar: "neutral",
  en_curso: "info",
  completada: "success",
};
// Gestión por excepción: lo que está en curso arriba, lo cerrado al fondo.
const RUTA_ESTADO_ORDEN = { en_curso: 0, sin_iniciar: 1, completada: 2 };

// Colores del gráfico desde los tokens del tema (recharts no resuelve var()
// dentro de atributos SVG, así que se leen computados y se re-leen al cambiar
// el tema).
function useChartColors(isDark) {
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const token = (name, fallback) => {
      const raw = css.getPropertyValue(name).trim();
      return raw ? `rgb(${raw})` : fallback;
    };
    return {
      real: token("--brand-500", "rgb(249 115 22)"),
      plan: isDark ? "#94a3b8" : "#64748b",
      grid: isDark ? "#374151" : "#E5E7EB",
      axis: isDark ? "#9ca3af" : "#64748b",
      tooltipBg: isDark ? "#1f2937" : "#ffffff",
      tooltipBorder: isDark ? "#374151" : "#e2e8f0",
      tooltipText: isDark ? "#f9fafb" : "#0f172a",
    };
  }, [isDark]);
}

// ─────────────────────────── vista principal ───────────────────────────

/**
 * MonitorTab — torre de seguimiento del día (port del tab "Monitoreo" del
 * mockup mu-portal, alimentado solo con datos reales del back):
 * KPIs-filtro, rutas en calle con avance, curva horaria real vs ventanas
 * comprometidas y carga por comuna. Sin ETA ni estados en_curso/fallida por
 * parada: el back aún no los emite, así que no se muestran cifras inventadas.
 */
export function MonitorTab({
  loading,
  instancias,
  recepciones,
  sinParada,
  selectedDate,
  onSelectedDateChange,
  todayYmd,
  onVerRuta,
}) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = useChartColors(isDark);
  const [corte, setCorte] = useState("");

  // Paradas aplanadas del día, con su ruta/chofer a mano para listas y cortes.
  const paradas = useMemo(() => {
    const out = [];
    for (const inst of instancias) {
      for (const p of inst.paradas) {
        out.push({
          ...p,
          ruta: inst.nombre,
          rutaId: inst.id,
          chofer: inst.chofer || "",
          sinChofer: !inst.chofer,
        });
      }
    }
    return out;
  }, [instancias]);

  const resumen = useMemo(() => {
    const total = paradas.length;
    const realizadas = paradas.filter(CORTE_PRED.realizadas).length;
    const anuladas = paradas.filter(CORTE_PRED.anuladas).length;
    const sinAsignar = paradas.filter(CORTE_PRED.sin_asignar).length;
    // Paradas que el repartidor todavía tiene que resolver. Es el denominador honesto
    // del avance: una anulada ya se resolvió, así que dejarla adentro haría que el día
    // nunca llegue al 100%.
    const exigibles = paradas.length - anuladas;
    // On-time real: retiros dentro de la ventana comprometida. Solo cuenta
    // paradas ejecutadas CON ventana y CON hora de retiro; si no hay ninguna,
    // el KPI muestra "—" en vez de un porcentaje inventado.
    let conVentana = 0;
    let enVentana = 0;
    for (const p of paradas) {
      if (p.estado !== "ejecutada") continue;
      const limite = hhmm(p.ventanaHasta);
      const primero = primerPickup(p.pickups);
      const hora = primero?.date ? horaDe(primero.date) : null;
      if (!limite || !hora) continue;
      conVentana += 1;
      if (hora <= limite) enVentana += 1;
    }
    return {
      total,
      exigibles,
      realizadas,
      anuladas,
      // Se cuenta con el predicado, no como `total - realizadas`: esa resta metía las
      // anuladas dentro de pendientes en silencio.
      pendientes: paradas.filter(CORTE_PRED.pendientes).length,
      sinAsignar,
      conVentana,
      enVentana,
      onTimePct: conVentana > 0 ? Math.round((enVentana / conVentana) * 100) : null,
      bultosPlan: sumNum(paradas, "bultosEsperados"),
    };
  }, [paradas]);

  // Rutas en calle: una fila por instancia del día, con avance y actividad real.
  const rutas = useMemo(() => {
    return instancias
      .map((inst) => {
        const total = inst.paradas.length;
        const realizadas = inst.paradas.filter((p) => p.estado === "ejecutada").length;
        const horas = inst.paradas
          .flatMap((p) => (p.pickups || []).map((pk) => pk.date))
          .filter(Boolean)
          .sort();
        const desde = inst.paradas.map((p) => hhmm(p.ventanaDesde)).filter(Boolean).sort();
        const hasta = inst.paradas.map((p) => hhmm(p.ventanaHasta)).filter(Boolean).sort();
        const estado =
          total > 0 && realizadas === total
            ? "completada"
            : realizadas > 0
              ? "en_curso"
              : "sin_iniciar";
        return {
          id: inst.id,
          nombre: inst.nombre,
          frecuencia: inst.frecuencia,
          chofer: inst.chofer,
          choferEmail: inst.choferEmail,
          total,
          realizadas,
          pendientes: total - realizadas,
          avancePct: total === 0 ? 0 : Math.round((realizadas / total) * 100),
          bultosPlan: sumNum(inst.paradas, "bultosEsperados"),
          primerRetiro: horas[0] || null,
          ultimoRetiro: horas.length > 1 ? horas[horas.length - 1] : null,
          ventanaDesde: desde[0] || null,
          ventanaHasta: hasta[hasta.length - 1] || null,
          estado,
        };
      })
      .sort(
        (a, b) =>
          RUTA_ESTADO_ORDEN[a.estado] - RUTA_ESTADO_ORDEN[b.estado] ||
          a.nombre.localeCompare(b.nombre),
      );
  }, [instancias]);

  // Curva del día en UNA sola unidad: paradas. "Real" = paradas retiradas,
  // acumuladas por la hora de su PRIMER retiro; "plan" = paradas cuya ventana
  // comprometía el retiro hasta esa hora. Contar pickups en real inflaba la
  // serie con el multi-retiro (una parada con 3 retiros sumaba 3 vs 1 del plan)
  // y el "vs plan" podía dar verde con el día atrasado.
  const curva = useMemo(() => {
    const realHoras = paradas
      .filter((p) => p.estado === "ejecutada")
      .map((p) => primerPickup(p.pickups)?.date)
      .filter(Boolean)
      .map((d) => new Date(d).getHours());
    // El plan excluye las anuladas: esa parada ya NO se va a retirar, así que dejarla
    // en la curva sube un techo que la línea real no puede alcanzar nunca y el "vs plan"
    // queda en rojo permanente por un compromiso que dejó de existir.
    const planHoras = paradas
      .filter((p) => p.estado !== "anulada")
      .map((p) => hhmm(p.ventanaHasta))
      .filter(Boolean)
      .map((s) => parseInt(s.slice(0, 2), 10));
    if (realHoras.length === 0 && planHoras.length === 0) return null;
    const todas = [...realHoras, ...planHoras];
    const min = Math.max(0, Math.min(...todas, 8));
    const max = Math.min(23, Math.max(...todas, 18));
    const points = [];
    for (let h = min; h <= max; h++) {
      const point = {
        hora: `${String(h).padStart(2, "0")}:00`,
        real: realHoras.filter((x) => x <= h).length,
      };
      if (planHoras.length > 0) point.plan = planHoras.filter((x) => x <= h).length;
      points.push(point);
    }
    return { points, hasPlan: planHoras.length > 0 };
  }, [paradas]);

  // Marca "Ahora" solo mirando el día de hoy (en otros días no significa nada).
  const ahoraLabel = useMemo(() => {
    if (selectedDate !== todayYmd || !curva) return null;
    const h = `${String(new Date().getHours()).padStart(2, "0")}:00`;
    return curva.points.some((p) => p.hora === h) ? h : null;
  }, [selectedDate, todayYmd, curva]);

  // Carga por comuna (top 8). Cada parada cae en un solo segmento.
  const comunas = useMemo(() => {
    const by = new Map();
    for (const p of paradas) {
      const nombre = p.comuna || t("Sin comuna");
      const c = by.get(nombre) || {
        comuna: nombre, total: 0, realizadas: 0, pendientes: 0, sinAsignar: 0, anuladas: 0,
      };
      c.total += 1;
      if (p.estado === "ejecutada") c.realizadas += 1;
      // Antes caían en `pendientes` por el else final, inflando la carga pendiente de la
      // comuna con paradas que ya no hay que visitar.
      else if (p.estado === "anulada") c.anuladas += 1;
      else if (p.sinChofer) c.sinAsignar += 1;
      else c.pendientes += 1;
      by.set(nombre, c);
    }
    return [...by.values()].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [paradas, t]);

  const retirosDelCorte = useMemo(
    () => (corte === "" ? [] : paradas.filter(CORTE_PRED[corte])),
    [corte, paradas],
  );

  // Sobre lo EXIGIBLE, no sobre el total: con una parada anulada el día podía quedar
  // clavado abajo del 100% sin que quedara nada por hacer.
  const avance =
    resumen.exigibles === 0 ? 0 : Math.round((resumen.realizadas / resumen.exigibles) * 100);
  const pctDe = (n) => (resumen.total === 0 ? 0 : (n / resumen.total) * 100);

  const tarjetas = [
    {
      valor: "",
      label: t("Retiros del día"),
      icono: PackageCheck,
      tono: "brand",
      cifra: `${formatNumberCL(resumen.realizadas)}/${formatNumberCL(resumen.exigibles)}`,
      pie:
        resumen.bultosPlan > 0
          ? t("{{pct}}% realizado · {{n}} bultos plan", { pct: avance, n: formatNumberCL(resumen.bultosPlan) })
          : t("{{pct}}% realizado", { pct: avance }),
      pct: avance,
      filtro: true,
    },
    {
      valor: "realizadas",
      label: t("Realizadas"),
      icono: CheckCircle2,
      tono: "success",
      cifra: formatNumberCL(resumen.realizadas),
      pie: t("Con retiro registrado"),
      pct: pctDe(resumen.realizadas),
      filtro: true,
    },
    {
      valor: "pendientes",
      label: t("Pendientes"),
      icono: Timer,
      tono: "warning",
      cifra: formatNumberCL(resumen.pendientes),
      pie: t("Aún sin retiro registrado"),
      pct: pctDe(resumen.pendientes),
      filtro: true,
    },
    {
      valor: "anuladas",
      label: t("Anuladas"),
      icono: CircleSlash,
      tono: "neutral",
      cifra: formatNumberCL(resumen.anuladas),
      pie: t("El comercio no tenía pedidos"),
      pct: pctDe(resumen.anuladas),
      filtro: true,
    },
    {
      valor: "sin_asignar",
      label: t("Sin asignar"),
      icono: MapPinOff,
      tono: resumen.sinAsignar > 0 ? "error" : "neutral",
      cifra: formatNumberCL(resumen.sinAsignar),
      pie:
        resumen.sinAsignar > 0
          ? t("Paradas de rutas sin chofer")
          : t("Todas las rutas tienen chofer"),
      pct: pctDe(resumen.sinAsignar),
      filtro: true,
    },
    {
      valor: "__ontime__",
      label: t("On-time del día"),
      icono: Clock,
      tono:
        resumen.onTimePct === null
          ? "neutral"
          : resumen.onTimePct >= 90
            ? "success"
            : resumen.onTimePct >= 80
              ? "warning"
              : "error",
      cifra: resumen.onTimePct === null ? "—" : `${resumen.onTimePct}%`,
      pie:
        resumen.onTimePct === null
          ? t("Sin ventanas comprometidas")
          : t("{{n}} paradas dentro de ventana", { n: formatNumberCL(resumen.enVentana) }),
      pct: resumen.onTimePct ?? 0,
      filtro: false,
    },
    {
      valor: "__bodega__",
      label: t("Colectas sin ruta"),
      icono: Warehouse,
      tono: "info",
      cifra: formatNumberCL(recepciones.length + sinParada.length),
      pie:
        sinParada.length > 0
          ? t("{{a}} en bodega · {{b}} sin parada", { a: recepciones.length, b: sinParada.length })
          : t("Recepciones directas en bodega"),
      pct: 0,
      filtro: false,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        <DayBar selectedDate={selectedDate} onSelectedDateChange={onSelectedDateChange} todayYmd={todayYmd} />
        <div className="rec-card rec-skel">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rec-skel__row" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DayBar selectedDate={selectedDate} onSelectedDateChange={onSelectedDateChange} todayYmd={todayYmd} />

      {/* ── Banda de KPIs (tarjetas-filtro) ── */}
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 xl:grid-cols-6">
        {tarjetas.map(({ icono: Icono, ...tj }) => {
          const esActivo = corte !== "" && corte === tj.valor;
          const estilo = TONO[tj.tono] ?? TONO.neutral;
          return (
            <button
              key={tj.valor || "__todos__"}
              type="button"
              disabled={!tj.filtro && tj.valor !== ""}
              onClick={() => {
                if (tj.valor === "" || !tj.filtro) {
                  setCorte("");
                  return;
                }
                setCorte(esActivo ? "" : tj.valor);
              }}
              aria-pressed={esActivo}
              className={cn(
                "group rounded-xl border p-[0.75rem] text-left transition-all",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
                esActivo ? estilo.activo : estilo.inactivo,
                !tj.filtro && tj.valor !== "" && "cursor-default",
              )}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", estilo.icono)}>
                  <Icono className="h-4 w-4" />
                </span>
                <span className="text-xl font-bold tabular-nums text-fg">{tj.cifra}</span>
              </div>
              <div className={cn("text-[11px] font-semibold uppercase tracking-wide", esActivo ? "text-fg" : "text-fg-muted")}>
                {tj.label}
              </div>
              <div className="mt-1 text-[11px] leading-snug text-fg-subtle line-clamp-2">{tj.pie}</div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-bg-muted">
                <div
                  className={cn("h-full transition-all", estilo.barra)}
                  style={{ width: `${Math.min(100, Math.max(0, tj.pct))}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Corte activo: lista compacta del KPI clickeado ── */}
      {corte !== "" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-border bg-bg-muted/40 px-4 py-2.5">
            <div className="flex items-center gap-2 text-sm">
              <Filter className="w-4 h-4 text-fg-muted" />
              <span className="text-fg-muted">{t("Corte activo")}</span>
              <Badge tone={CORTE_TONE[corte]} dot>
                {t(CORTE_LABEL[corte])}
              </Badge>
              <span className="text-fg-muted tabular-nums">
                {t("{{x}} de {{n}} retiros", { x: retirosDelCorte.length, n: resumen.total })}
              </span>
            </div>
            <button className="rec-btn rec-btn--ghost rec-btn--sm" onClick={() => setCorte("")}>
              <X size={14} />
              {t("Limpiar corte")}
            </button>
          </div>
          <RetirosCompactList retiros={retirosDelCorte} titulo={t(CORTE_TITULO[corte])} />
        </div>
      )}

      {resumen.total === 0 ? (
        <div className="rec-card">
          <EmptyState
            icon={<RouteIcon />}
            title={t("Sin rutas para este día")}
            description={t("Ninguna ruta de retiro está programada para esta fecha (según su frecuencia).")}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2 min-w-0">
            <RutasEnCalle rutas={rutas} onVerRuta={onVerRuta} />
          </div>
          <div className="space-y-4 min-w-0">
            <CurvaHoraria curva={curva} colors={colors} ahoraLabel={ahoraLabel} />
            <ComunasBar data={comunas} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Rutas en calle ───────────────────────────

function RutasEnCalle({ rutas, onVerRuta }) {
  const { t } = useTranslation();
  const enCurso = rutas.filter((r) => r.estado === "en_curso").length;
  return (
    <div className="rounded-xl border border-border bg-bg shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-fg">{t("Rutas en calle")}</h2>
          <p className="mt-0.5 text-sm text-fg-muted">
            {t("{{n}} rutas del día · {{x}} en curso", { n: rutas.length, x: enCurso })}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-fg-muted">
          <span className="inline-flex items-center gap-1">
            <i className="h-2 w-2 rounded-full bg-status-success" /> {t("Realizadas")}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="h-2 w-2 rounded-full bg-bg-subtle ring-1 ring-border" /> {t("Pendientes")}
          </span>
        </div>
      </header>
      <Table>
        <THead>
          <TR>
            <TH>{t("Ruta")}</TH>
            <TH>{t("Chofer")}</TH>
            <TH className="min-w-[180px]">{t("Avance")}</TH>
            <TH>{t("Ventana")}</TH>
            <TH>{t("Actividad")}</TH>
            <TH>{t("Estado")}</TH>
            <TH className="w-8" />
          </TR>
        </THead>
        <TBody>
          {rutas.map((r) => (
            <TR
              key={r.id}
              onClick={() => onVerRuta?.(r)}
              className="cursor-pointer align-middle"
              title={t("Ver esta ruta en Planificación")}
            >
              <TD className="align-middle">
                <div className="font-semibold text-fg">{r.nombre}</div>
                {r.frecuencia && <div className="text-xs text-fg-muted">{t(r.frecuencia)}</div>}
              </TD>
              <TD className="align-middle">
                {r.chofer ? (
                  <div className="flex items-center gap-2">
                    <span className="rec-avatar rec-avatar--sm">{iniciales(r.chofer)}</span>
                    <div className="min-w-0">
                      <div className="truncate text-sm text-fg">{r.chofer}</div>
                      {r.choferEmail && (
                        <div className="truncate text-xs text-fg-subtle max-w-[160px]">{r.choferEmail}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Badge tone="error">{t("Sin chofer")}</Badge>
                )}
              </TD>
              <TD className="align-middle">
                <div
                  className="flex h-2 w-full overflow-hidden rounded-full bg-bg-muted"
                  title={`${r.realizadas} ${t("realizadas")} · ${r.pendientes} ${t("pendientes")}`}
                >
                  {r.realizadas > 0 && (
                    <div className="h-full bg-status-success transition-all" style={{ width: `${(r.realizadas / Math.max(1, r.total)) * 100}%` }} />
                  )}
                  {r.pendientes > 0 && (
                    <div className="h-full bg-bg-subtle transition-all" style={{ width: `${(r.pendientes / Math.max(1, r.total)) * 100}%` }} />
                  )}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs tabular-nums text-fg-muted">
                  <span>
                    {r.realizadas}/{r.total} {t("paradas")}
                  </span>
                  <span className="font-medium text-fg">{r.avancePct}%</span>
                </div>
                {r.bultosPlan > 0 && (
                  <div className="text-[11px] tabular-nums text-fg-subtle">
                    {t("{{n}} bultos plan", { n: formatNumberCL(r.bultosPlan) })}
                  </div>
                )}
              </TD>
              <TD className="align-middle whitespace-nowrap text-sm tabular-nums text-fg-muted">
                {r.ventanaDesde && r.ventanaHasta ? `${r.ventanaDesde}–${r.ventanaHasta}` : "—"}
              </TD>
              <TD className="align-middle whitespace-nowrap">
                {r.primerRetiro ? (
                  <>
                    <div className="text-sm tabular-nums text-fg">
                      {horaDe(r.primerRetiro)}
                      {r.ultimoRetiro && ` – ${horaDe(r.ultimoRetiro)}`}
                    </div>
                    <div className="text-xs text-fg-subtle">
                      {r.ultimoRetiro ? t("primer y último retiro") : t("único retiro")}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-fg-subtle">{t("Sin retiros aún")}</span>
                )}
              </TD>
              <TD className="align-middle">
                <Badge tone={RUTA_ESTADO_TONE[r.estado]} dot>
                  {t(RUTA_ESTADO_LABEL[r.estado])}
                </Badge>
              </TD>
              <TD className="align-middle">
                <ChevronRight className="h-4 w-4 text-fg-subtle" aria-hidden />
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

// ─────────────────────────── Lista compacta del corte ───────────────────────────

function RetirosCompactList({ retiros, titulo }) {
  const { t } = useTranslation();
  const { pdfLink, pdfModal } = usePdfViewer();
  return (
    <div className="rounded-xl border border-border bg-bg shadow-card">
      {pdfModal}
      <header className="border-b border-border px-5 py-3">
        <h3 className="text-sm font-semibold text-fg">{titulo}</h3>
      </header>
      {retiros.length === 0 ? (
        <p className="px-5 py-4 text-sm text-fg-muted">{t("Nada en este corte para el día seleccionado.")}</p>
      ) : (
        <ul className="divide-y divide-border max-h-[380px] overflow-y-auto">
          {retiros.map((p, i) => {
            const primero = primerPickup(p.pickups);
            return (
            <li key={`${p.rutaId}|${p.id}|${i}`} className="flex items-center gap-3 px-5 py-2.5">
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  p.estado === "ejecutada"
                    ? "bg-status-success"
                    // Anulada antes que sinChofer: que la ruta no tenga chofer asignado
                    // es irrelevante en una parada que ya se resolvió.
                    : p.estado === "anulada"
                      ? "bg-transparent ring-1 ring-inset ring-status-neutral"
                      : p.sinChofer
                        ? "bg-status-error"
                        : "bg-status-neutral",
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-fg">{p.comercio}</div>
                <div className="truncate text-xs text-fg-muted">
                  {[p.direccion, p.comuna].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="hidden sm:block min-w-0 max-w-[180px] text-right">
                <div className="truncate text-xs text-fg-muted">{p.ruta}</div>
                <div className="truncate text-xs text-fg-subtle">{p.chofer || t("Sin chofer")}</div>
              </div>
              <div className="shrink-0 text-right">
                {primero ? (
                  <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-fg">
                    <span className="rec-mono">#{primero.correlative}</span>
                    <Clock size={11} aria-hidden /> {horaDe(primero.date)}
                    {primero.pdfUrl && (
                      pdfLink({
                        url: primero.pdfUrl,
                        correlative: primero.correlative,
                        className: "rec-link",
                        title: t("Ver PDF"),
                        children: <FileText size={12} />,
                      })
                    )}
                  </span>
                ) : (
                  <span className="text-xs tabular-nums text-fg-muted">
                    {hhmm(p.ventanaDesde) && hhmm(p.ventanaHasta)
                      ? `${hhmm(p.ventanaDesde)}–${hhmm(p.ventanaHasta)}`
                      : "—"}
                  </span>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────── Curva del día ───────────────────────────

function CurvaHoraria({ curva, colors, ahoraLabel }) {
  const { t } = useTranslation();
  if (!curva) {
    return (
      <div className="rounded-xl border border-border bg-bg p-[1rem] shadow-card">
        <h2 className="text-base font-semibold text-fg">{t("Curva del día")}</h2>
        <p className="mt-1 text-sm text-fg-muted">
          {t("Sin retiros ni ventanas horarias para graficar este día.")}
        </p>
      </div>
    );
  }
  const ultimo = curva.points[curva.points.length - 1];
  const brecha = curva.hasPlan ? (ultimo?.real ?? 0) - (ultimo?.plan ?? 0) : null;
  return (
    <div className="rounded-xl border border-border bg-bg p-[1rem] shadow-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-fg">{t("Curva del día")}</h2>
          <p className="text-sm text-fg-muted">{t("Paradas retiradas acumuladas · real vs ventanas comprometidas")}</p>
        </div>
        {brecha !== null && (
          <div className="text-right">
            <div
              className={cn(
                "text-lg font-bold tabular-nums",
                brecha < 0 ? "text-status-error" : "text-status-success",
              )}
            >
              {brecha > 0 ? `+${brecha}` : brecha}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{t("vs plan")}</div>
          </div>
        )}
      </div>
      <div className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* left -8 (no -22 como el mockup): con cientos de retiros el eje Y
              tiene 3 dígitos y el margen negativo grande los recortaba. */}
          <ComposedChart data={curva.points} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="recCurvaReal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.real} stopOpacity={0.35} />
                <stop offset="95%" stopColor={colors.real} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
            <XAxis dataKey="hora" stroke={colors.axis} fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke={colors.axis} fontSize={11} tickLine={false} axisLine={false} width={44} allowDecimals={false} />
            <ChartTooltip
              contentStyle={{
                backgroundColor: colors.tooltipBg,
                border: `1px solid ${colors.tooltipBorder}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: colors.tooltipText, fontWeight: 600 }}
              formatter={(value, name) => [t("{{n}} paradas", { n: value }), name]}
            />
            {ahoraLabel && (
              <ReferenceLine
                x={ahoraLabel}
                stroke={colors.axis}
                strokeDasharray="4 3"
                label={{
                  value: `${t("Ahora")} ${ahoraLabel}`,
                  position: "insideTopRight",
                  fontSize: 10,
                  fill: colors.axis,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="real"
              name={t("Real")}
              stroke={colors.real}
              strokeWidth={2}
              fill="url(#recCurvaReal)"
              connectNulls
            />
            {curva.hasPlan && (
              <Line
                type="monotone"
                dataKey="plan"
                name={t("Plan")}
                stroke={colors.plan}
                strokeWidth={2}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-0.5 w-4 rounded-full" style={{ backgroundColor: colors.real }} />
          {t("Paradas retiradas")}
        </span>
        {curva.hasPlan && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-0.5 w-4 rounded-full" style={{ backgroundColor: colors.plan }} />
            {t("Plan por ventana horaria")}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Carga por comuna ───────────────────────────

const COMUNA_SEGMENTOS = [
  { clave: "realizadas", label: "realizadas", cls: "bg-status-success" },
  { clave: "pendientes", label: "pendientes", cls: "bg-bg-subtle" },
  { clave: "sinAsignar", label: "sin asignar", cls: "bg-status-error/45" },
];

function ComunasBar({ data }) {
  const { t } = useTranslation();
  const maximo = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="rounded-xl border border-border bg-bg p-[1rem] shadow-card">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-fg">{t("Carga por comuna")}</h2>
        <p className="text-sm text-fg-muted">
          {t("Top {{n}} por volumen · desglose por estado", { n: data.length })}
        </p>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("Sin paradas para este día.")}</p>
      ) : (
        <>
          <ul className="space-y-2.5">
            {data.map((c) => (
              <li key={c.comuna}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate font-medium text-fg">{c.comuna}</span>
                  <span className="shrink-0 tabular-nums text-fg-muted">
                    {c.total}
                    {c.sinAsignar > 0 && (
                      <span className="ml-1.5 text-status-error">
                        · {t("{{n}} sin asignar", { n: c.sinAsignar })}
                      </span>
                    )}
                  </span>
                </div>
                <div
                  className="mt-1 flex h-2.5 overflow-hidden rounded-full bg-bg-muted"
                  style={{ width: `${Math.max(18, (c.total / maximo) * 100)}%` }}
                  title={COMUNA_SEGMENTOS.map((s) => `${c[s.clave]} ${t(s.label)}`).join(" · ")}
                >
                  {COMUNA_SEGMENTOS.map((s) =>
                    c[s.clave] === 0 ? null : (
                      <div
                        key={s.clave}
                        className={cn("h-full", s.cls)}
                        style={{ width: `${(c[s.clave] / c.total) * 100}%` }}
                      />
                    ),
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-3 text-[11px] text-fg-muted">
            {COMUNA_SEGMENTOS.map((s) => (
              <span key={s.clave} className="inline-flex items-center gap-1.5">
                <i className={cn("h-2 w-2 rounded-full", s.cls, s.clave === "pendientes" && "ring-1 ring-border")} />
                {t(s.label)}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
